/**
 * BulkUploadStackContext — pile globale d'uploads en arrière-plan (S5b+).
 *
 * **Pourquoi.** Avant ce pattern, chaque modal d'upload (œuvre, mockup) hébergeait
 * son propre `useBulkUpload` local. Conséquence : fermer la modal = démontage
 * du hook = `useEffect` cleanup qui pose `cancelledRef.current = true` = pool
 * `_runWithConcurrency` arrêté au prochain pull = fichiers en file d'attente
 * jamais envoyés. Incident 2026-05-09 sur l'œuvre 29 (3 fichiers sur 4 perdus).
 *
 * Le provider hisse l'état au niveau `<AppShell>` : un seul singleton de pile,
 * survit aux changements de route et au démontage des composants caller. Style
 * Google Drive — l'utilisatrice peut continuer à travailler pendant que ça
 * upload.
 *
 * **Verrou ESLint** `bulk-upload-must-use-stack` : `_runWithConcurrency` est
 * réservé au provider. Pour orchestrer un upload en masse, utiliser
 * `useBulkUploadStack().enqueue()`.
 *
 * Ce module exporte uniquement les types + createContext + le hook — la JSX
 * provider vit dans `BulkUploadStackContext.tsx` (séparation Fast Refresh).
 */
import { createContext, useContext } from 'react'

// ──────────────────────────────────────────────────────────────────────────
// Types per-file (ré-exposés pour les consommateurs — l'ancien
// `useBulkUpload` les hébergeait, on les déplace ici puisqu'il est supprimé).
// ──────────────────────────────────────────────────────────────────────────

export type BulkFileStatus =
  | 'pending'
  | 'uploading'
  /** Création réussie. */
  | 'success'
  /**
   * Fichier déjà uploadé (sha256 identique). Succès idempotent — aucune
   * action requise. Compté avec les succès dans l'UI.
   */
  | 'duplicate_content'
  /**
   * Fichier qui aurait dû être créé mais entre en collision sur un slug
   * tiers (nom de fichier déjà pris par un autre contenu). Action
   * requise = renommer côté disque puis re-drag. Spécifique mockup.
   */
  | 'duplicate_slug'
  /** Erreur serveur ou réseau (action requise). */
  | 'error'

export interface BulkFileProgress {
  filename: string
  status: BulkFileStatus
  /** Message à afficher dans le tableau d'erreurs en phase done. */
  message?: string
}

export interface BulkUploadCounts {
  pending: number
  uploading: number
  success: number
  duplicate_content: number
  duplicate_slug: number
  error: number
}

export interface BulkUploadSummary {
  counts: BulkUploadCounts
  /** Fichiers à recharger sur retry (status `error` ou `duplicate_slug`). */
  retryFiles: File[]
}

export interface MappedError {
  status: BulkFileStatus
  message: string
}

// ──────────────────────────────────────────────────────────────────────────
// Constantes partagées
// ──────────────────────────────────────────────────────────────────────────

export const EMPTY_COUNTS: BulkUploadCounts = {
  pending: 0,
  uploading: 0,
  success: 0,
  duplicate_content: 0,
  duplicate_slug: 0,
  error: 0,
}

/** Recalcule les compteurs depuis le tableau de progress (utilisé par le
 * provider à chaque update — pas de variable accumulée à part). */
export function countByStatus(rows: readonly BulkFileProgress[]): BulkUploadCounts {
  const counts = { ...EMPTY_COUNTS }
  for (const row of rows) counts[row.status] += 1
  return counts
}

// ──────────────────────────────────────────────────────────────────────────
// Shape d'un batch dans la pile
// ──────────────────────────────────────────────────────────────────────────

export interface BulkUploadBatch {
  /** uuid v4 — identifiant stable du batch dans la pile. */
  id: string
  /** Visible dans l'overlay : "Œuvre #29 — 4 images". */
  label: string
  /** Total de fichiers — figé à l'enqueue. */
  total: number
  phase: 'uploading' | 'done'
  progress: readonly BulkFileProgress[]
  counts: BulkUploadCounts
  /** True si l'utilisatrice a cliqué "Annuler" en cours. */
  cancelled: boolean
  /** Date.now() — pour ordre d'affichage et timer auto-dismiss. */
  startedAt: number
}

// ──────────────────────────────────────────────────────────────────────────
// Input enqueue + API du context
// ──────────────────────────────────────────────────────────────────────────

export interface EnqueueInput<TResult = unknown> {
  /** Visible dans l'overlay (ex: "Œuvre #29 — 4 images"). */
  label: string
  files: File[]
  uploader: (file: File) => Promise<TResult>
  /** Default 2. Pour les RAW d'œuvre, passer 1 (bande passante client). */
  concurrencyLimit?: number
  /** Map `ApiError` → `MappedError`. Default : tout = `error`. */
  mapError?: (error: unknown, file: File) => MappedError
  /** Appelé en fin de batch (succès complet OU cancel — summary partiel
   * dans ce dernier cas, permet d'invalider les queries pour voir les
   * fichiers déjà uploadés). */
  onComplete?: (summary: BulkUploadSummary) => void
}

export interface BulkUploadStackContextValue {
  batches: readonly BulkUploadBatch[]
  /** Démarre un batch en arrière-plan, retourne son id. */
  enqueue: <T>(input: EnqueueInput<T>) => string
  /** Retire un batch `done` du widget. No-op si batch absent ou
   * `phase === 'uploading'` (cancel d'abord). */
  dismiss: (id: string) => void
  /** Pose le flag d'annulation. Workers en flight finissent côté serveur
   * (idempotent BE), pas de nouveau pull. Le batch passe en `done`
   * avec `cancelled: true`. */
  cancel: (id: string) => void
  /** Re-enqueue les fichiers `error`/`duplicate_slug` du batch dans un
   * nouveau batch indépendant. Dismiss l'ancien. Retourne l'id du
   * nouveau batch ou null si le source n'est pas en `done` ou n'a pas
   * d'échec à retenter. */
  retry: (id: string) => string | null
}

export const BulkUploadStackContext = createContext<BulkUploadStackContextValue | null>(null)

export function useBulkUploadStack(): BulkUploadStackContextValue {
  const ctx = useContext(BulkUploadStackContext)
  if (!ctx) {
    throw new Error(
      'useBulkUploadStack must be used within a <BulkUploadStackProvider>. ' +
        'Wrap your App or Page in <BulkUploadStackProvider>.',
    )
  }
  return ctx
}
